import { Injectable, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { distinctUntilChanged } from 'rxjs/operators';
import { UnifiedAnalyticsService } from './unified-analytics.service';
import { UserService } from './user.service';

@Injectable({
    providedIn: 'root',
})
export class AnalyticsService {
    private readonly unifiedAnalyticsService = inject(UnifiedAnalyticsService);
    private readonly userService = inject(UserService);
    private readonly destroyRef = inject(DestroyRef);

    constructor() {
        this.setupUserTracking();
    }

    private setupUserTracking(): void {
        this.userService.currentUser$
            .pipe(
                distinctUntilChanged((prev, curr) => prev?.userId === curr?.userId),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((user) => {
                if (user) {
                    this.unifiedAnalyticsService.identifyUser(user);
                } else {
                    this.unifiedAnalyticsService.userSignedOut();
                }
            });
    }

    track(eventName: string, properties?: Record<string, any>): void {
        this.unifiedAnalyticsService.track(eventName, properties);
    }

    trackUserAction(action: string, category?: string, label?: string, value?: number): void {
        this.unifiedAnalyticsService.track('user_action', {
            action,
            category: category || 'general',
            label,
            value,
        });
    }

    trackFeatureUsage(featureName: string, properties?: Record<string, any>): void {
        this.unifiedAnalyticsService.track('feature_usage', {
            feature_name: featureName,
            ...properties,
        });
    }

    trackError(error: string, category?: string): void {
        this.unifiedAnalyticsService.track('error', {
            error_message: error,
            error_category: category || 'general',
        });
    }

    getStatus(): any {
        return this.unifiedAnalyticsService.getStatus();
    }

    get isInitialized(): boolean {
        return this.unifiedAnalyticsService.initialized();
    }
}
